/**
 * Permits API (Genehmigungsverfahren)
 * CRUD-Operationen für Genehmigungsverfahren, Antragsbausteine und Nachweise
 */
import { api } from "./client";
import {
  AntragVorbelegung,
  ApplicationComponent,
  ApplicationComponentCategoryOption,
  ApplicationComponentCreate,
  ApplicationComponentTypeOption,
  ApplicationComponentUpdate,
  Evidence,
  EvidenceCatalogOption,
  EvidenceCreate,
  EvidenceHistorieEintrag,
  EvidenceUebergang,
  EvidenceUpdate,
  EvidenceTypeOption,
  LookupOption,
  ProcessingPathOption,
  ProcedureTypeOption,
  PermitCostItem,
  PermitCostItemCreate,
  PermitCostItemUpdate,
  PermitCostSummary,
  PermitProcedure,
  ProcedureTransition,
  PermitProcedureCreate,
  PermitProcedureHistoryEntry,
  PermitKontakt,
  PermitKontaktCreate,
  PermitKontaktFelder,
  PermitProcedureReference,
  EvidenceExpert,
  PermitProcedureUpdate,
  PermitSummary,
  ProjectEvidence,
  StandortmerkmalOption,
  UvpStatusOption,
  ProjectPermitCostItem,
  WindenergiegebietOption,
} from "../../types";

export const permitsApi = {
  /**
   * Options-Endpunkte (globale Referenzdaten, kein Projektbezug)
   */
  async getProcedureTypeOptions(): Promise<ProcedureTypeOption[]> {
    return api.get("/permits/procedure-type-options");
  },

  /**
   * Alle Anträge des Mandanten als Auswahl für die Bezugsgenehmigung – projektübergreifend,
   * weil ein Repowering-Antrag sich auf den Altpark und damit oft auf ein anderes Projekt
   * bezieht.
   */
  async getKontaktRolleOptions(): Promise<LookupOption[]> {
    return api.get("/permits/kontakt-rolle-options");
  },

  async getKontakte(
    projectId: string,
    permitProcedureId: string,
  ): Promise<PermitKontakt[]> {
    return api.get(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/kontakte`,
    );
  },

  async createKontakt(
    projectId: string,
    permitProcedureId: string,
    data: PermitKontaktCreate,
  ): Promise<PermitKontakt> {
    return api.post(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/kontakte`,
      data,
    );
  },

  async updateKontakt(
    projectId: string,
    permitProcedureId: string,
    kontaktId: string,
    data: PermitKontaktFelder,
  ): Promise<PermitKontakt> {
    return api.put(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/kontakte/${kontaktId}`,
      data,
    );
  },

  async deleteKontakt(
    projectId: string,
    permitProcedureId: string,
    kontaktId: string,
  ): Promise<void> {
    return api.delete(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/kontakte/${kontaktId}`,
    );
  },

  async getProcedureReferences(): Promise<PermitProcedureReference[]> {
    return api.get("/permits/procedure-references");
  },

  /** Gutachter des Mandanten, zuletzt verwendete zuerst – Externe bekommen keine (403) */
  async getEvidenceExperts(): Promise<EvidenceExpert[]> {
    return api.get("/permits/evidence-experts");
  },

  /** Institutionen des Mandanten (Anfordern von, extern), zuletzt verwendete zuerst */
  async getEvidenceInstitutions(): Promise<EvidenceExpert[]> {
    return api.get("/permits/evidence-institutions");
  },

  async getApplicationComponentCategoryOptions(): Promise<
    ApplicationComponentCategoryOption[]
  > {
    return api.get("/permits/application-component-category-options");
  },

  async getApplicationComponentTypeOptions(): Promise<
    ApplicationComponentTypeOption[]
  > {
    return api.get("/permits/application-component-type-options");
  },

  /** Nachweisarten, je mit dem Bearbeitungsweg, den sie vorbelegt */
  async getEvidenceTypeOptions(): Promise<EvidenceTypeOption[]> {
    return api.get("/permits/evidence-type-options");
  },

  /** Die Bearbeitungswege mit Namen und Einzeiler */
  async getProcessingPathOptions(): Promise<ProcessingPathOption[]> {
    return api.get("/permits/processing-path-options");
  },

  /** Nachweiskatalog in Katalogreihenfolge – die Auswahl beim Anlegen eines Nachweises */
  async getEvidenceCatalogOptions(): Promise<EvidenceCatalogOption[]> {
    return api.get("/permits/evidence-catalog-options");
  },

  /**
   * Holt die auswählbaren Vorhabenbestandteile (Umspannwerk, Batteriespeicher …).
   * Windenergieanlagen sind bewusst kein Wert – sie wirken über die Antragsart.
   */
  async getVorhabenbestandteilOptions(): Promise<LookupOption[]> {
    return api.get("/permits/vorhabenbestandteil-options");
  },

  async getWindenergiegebietOptions(): Promise<WindenergiegebietOption[]> {
    return api.get("/permits/windenergiegebiet-options");
  },

  /** Besonderheiten des Standorts, nach Kategorie sortiert */
  async getStandortmerkmalOptions(): Promise<StandortmerkmalOption[]> {
    return api.get("/permits/standortmerkmal-options");
  },

  /** Ausprägungen der Entscheidung (erteilt, versagt) – Auswahl im Dialog „Entscheidung erfassen“ */
  async getProcedureDecisionOptions(): Promise<LookupOption[]> {
    return api.get("/permits/procedure-decision-options");
  },

  async getUvpStatusOptions(): Promise<UvpStatusOption[]> {
    return api.get("/permits/uvp-status-options");
  },

  /**
   * Genehmigungsverfahren
   */
  async getPermitProcedures(projectId: string): Promise<PermitProcedure[]> {
    return api.get(`/projects/${projectId}/permit-procedures`);
  },

  /** Zusammenfassung der Anträge für die aufgeklappte Projektzeile */
  async getPermitSummary(projectId: string): Promise<PermitSummary> {
    return api.get(`/projects/${projectId}/permit-procedures/summary`);
  },

  /** Vorbelegung für einen neuen Antrag; mit `anlagenzahl` für eine geänderte Anlagenauswahl */
  async getAntragVorbelegung(
    projectId: string,
    anlagenzahl?: number,
  ): Promise<AntragVorbelegung> {
    const abfrage =
      anlagenzahl !== undefined ? `?anlagenzahl=${anlagenzahl}` : "";
    return api.get(
      `/projects/${projectId}/permit-procedures/vorbelegung${abfrage}`,
    );
  },

  async createPermitProcedure(
    projectId: string,
    data: PermitProcedureCreate,
  ): Promise<PermitProcedure> {
    return api.post(`/projects/${projectId}/permit-procedures`, data);
  },

  async updatePermitProcedure(
    projectId: string,
    permitProcedureId: string,
    data: PermitProcedureUpdate,
  ): Promise<PermitProcedure> {
    return api.put(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}`,
      data,
    );
  },

  /** Führt eine Aktion am Antrag aus – Statusübergang, Fristverlängerung, Rücknahme (ADR 011) */
  async transitionPermitProcedure(
    projectId: string,
    permitProcedureId: string,
    data: ProcedureTransition,
  ): Promise<PermitProcedure> {
    return api.post(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/transitions`,
      data,
    );
  },

  async deletePermitProcedure(
    projectId: string,
    permitProcedureId: string,
  ): Promise<void> {
    return api.delete(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}`,
    );
  },

  /**
   * Antragsbausteine
   */
  async getApplicationComponents(
    projectId: string,
    permitProcedureId: string,
  ): Promise<ApplicationComponent[]> {
    return api.get(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/application-components`,
    );
  },

  async createApplicationComponent(
    projectId: string,
    permitProcedureId: string,
    data: ApplicationComponentCreate,
  ): Promise<ApplicationComponent> {
    return api.post(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/application-components`,
      data,
    );
  },

  /**
   * Aktualisiert einen Antragsbaustein.
   *
   * Wird `ist_aktiv` mit einem anderen als dem bisherigen Wert gesendet, setzt der Server
   * `ist_aktiv_manuell` auf true.
   */
  async updateApplicationComponent(
    projectId: string,
    permitProcedureId: string,
    componentId: string,
    data: ApplicationComponentUpdate,
  ): Promise<ApplicationComponent> {
    return api.put(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/application-components/${componentId}`,
      data,
    );
  },

  async deleteApplicationComponent(
    projectId: string,
    permitProcedureId: string,
    componentId: string,
  ): Promise<void> {
    return api.delete(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/application-components/${componentId}`,
    );
  },

  /**
   * Zuordnung Baustein → Nachweis (ADR 006). Beide Aufrufe antworten mit dem Nachweis samt
   * seiner aktuellen Bausteinliste.
   */
  /**
   * Antragsübergreifende Listen des Projekts.
   *
   * Für die Seiten "Nachweise" und "Kosten", die den Überblick über alle Anträge zeigen.
   * Geschrieben wird weiterhin über die antragsbezogenen Endpunkte darunter – die Antrags-ID
   * steht dafür in jeder Zeile.
   */
  async getProjectEvidences(projectId: string): Promise<ProjectEvidence[]> {
    return api.get(`/projects/${projectId}/permit-evidences`);
  },

  async getProjectCostItems(
    projectId: string,
  ): Promise<ProjectPermitCostItem[]> {
    return api.get(`/projects/${projectId}/permit-cost-items`);
  },

  /**
   * Nachweise
   */
  async getEvidences(
    projectId: string,
    permitProcedureId: string,
  ): Promise<Evidence[]> {
    return api.get(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/evidences`,
    );
  },

  async createEvidence(
    projectId: string,
    permitProcedureId: string,
    data: EvidenceCreate,
  ): Promise<Evidence> {
    return api.post(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/evidences`,
      data,
    );
  },

  async updateEvidence(
    projectId: string,
    permitProcedureId: string,
    evidenceId: string,
    data: EvidenceUpdate,
  ): Promise<Evidence> {
    return api.put(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/evidences/${evidenceId}`,
      data,
    );
  },

  /** Führt eine Aktion am Nachweis aus (Statusübergang, Archivieren – ADR 007) */
  async uebergangEvidence(
    projectId: string,
    permitProcedureId: string,
    evidenceId: string,
    data: EvidenceUebergang,
  ): Promise<Evidence> {
    return api.post(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/evidences/${evidenceId}/uebergaenge`,
      data,
    );
  },

  /** Nachweishistorie, neueste zuerst */
  /** Antragshistorie, neueste zuerst */
  async getPermitProcedureHistory(
    projectId: string,
    permitProcedureId: string,
  ): Promise<PermitProcedureHistoryEntry[]> {
    return api.get(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/history`,
    );
  },

  async getEvidenceHistorie(
    projectId: string,
    permitProcedureId: string,
    evidenceId: string,
  ): Promise<EvidenceHistorieEintrag[]> {
    return api.get(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/evidences/${evidenceId}/historie`,
    );
  },

  async deleteEvidence(
    projectId: string,
    permitProcedureId: string,
    evidenceId: string,
  ): Promise<void> {
    return api.delete(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/evidences/${evidenceId}`,
    );
  },

  /**
   * Kostenpositionen eines Genehmigungsverfahrens
   */
  async getCostItems(
    projectId: string,
    permitProcedureId: string,
  ): Promise<PermitCostItem[]> {
    return api.get(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/cost-items`,
    );
  },

  async getCostSummary(
    projectId: string,
    permitProcedureId: string,
  ): Promise<PermitCostSummary> {
    return api.get(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/cost-items/summary`,
    );
  },

  async createCostItem(
    projectId: string,
    permitProcedureId: string,
    data: PermitCostItemCreate,
  ): Promise<PermitCostItem> {
    return api.post(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/cost-items`,
      data,
    );
  },

  async updateCostItem(
    projectId: string,
    permitProcedureId: string,
    costItemId: string,
    data: PermitCostItemUpdate,
  ): Promise<PermitCostItem> {
    return api.put(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/cost-items/${costItemId}`,
      data,
    );
  },

  async deleteCostItem(
    projectId: string,
    permitProcedureId: string,
    costItemId: string,
  ): Promise<void> {
    return api.delete(
      `/projects/${projectId}/permit-procedures/${permitProcedureId}/cost-items/${costItemId}`,
    );
  },
};
